import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ErrorTextField } from "@/components/atoms";
import { DialogComponentProps, useDemo } from "@/context";
import { useAudienceMutations, useAudiences } from "@/hooks";

type EditAudienceDialogProps = DialogComponentProps<"editAudience">;

const EditAudienceDialog: React.FC<EditAudienceDialogProps> = ({
  open,
  onClose,
  audience,
}) => {
  const { audiences } = useAudiences(audience.audience_type);
  const { isDemoMode } = useDemo();
  const { rename, action } = useAudienceMutations(audience);
  const { audience_name: audienceName, project_name: projectName } = audience;

  const [newAudienceName, setNewAudienceName] = useState<string>("");
  const [newProjectName, setNewProjectName] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const projects = useMemo(
    () =>
      Array.from(
        new Set(
          audiences
            ?.filter((a) => a.project_name)
            .map((a) => a.project_name as string)
            .sort((a, b) => a.localeCompare(b)),
        ),
      ),
    [audiences],
  );

  // Reset state when dialog opens or props change
  useEffect(() => {
    setNewAudienceName(audienceName);
    projectName && setNewProjectName(projectName);
    setError(false);
  }, [open, audienceName, projectName]);

  // Validate audience name uniqueness (excluding current)
  useEffect(() => {
    if (!newAudienceName.trim()) {
      setError(false);
      return;
    }
    const exists =
      audiences?.some(
        (audience) =>
          audience.audience_name === newAudienceName &&
          newAudienceName !== audienceName,
      ) ?? false;
    setError(exists);
  }, [newAudienceName, audiences, audienceName]);

  const handleAudienceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAudienceName(e.target.value);
  };

  const handleProjectNameChange = (
    _event: React.SyntheticEvent,
    value: string | null,
  ) => {
    setNewProjectName(value);
  };

  const handleProjectInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setNewProjectName(value.trim());
  };

  const handleSaveAudience = () => {
    rename(
      newAudienceName.trim(),
      newProjectName && newProjectName !== "" ? newProjectName.trim() : null,
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="edit-audience-dialog"
    >
      <DialogTitle>
        <Typography>Rename Audience</Typography>
      </DialogTitle>
      <DialogContent sx={{ width: 300 }}>
        <Stack spacing={2}>
          <Stack>
            <ErrorTextField
              error={error}
              variant="outlined"
              label="Audience Name"
              size="small"
              color="primary"
              placeholder="Audience Name"
              value={newAudienceName}
              onChange={handleAudienceNameChange}
              sx={{ mt: 1 }}
            />
            {error && (
              <Typography variant="caption" color="error">
                Audience name already exists
              </Typography>
            )}
          </Stack>
          <Autocomplete
            freeSolo
            value={newProjectName}
            onChange={handleProjectNameChange}
            inputValue={newProjectName ?? ""}
            onInputChange={handleProjectInputChange}
            options={projects}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : ""
            }
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Project Name (Optional)"
                size="small"
              />
            )}
            disabled={isDemoMode}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveAudience}
          disabled={error || !newAudienceName.trim()}
          loading={action.status === 'loading'}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAudienceDialog;
